import Errors from "../../components/Errors";
import PaymentSelection from "../../components/PaymentSelection";
import ShippingDropdown from "../../components/ShippingDropdown";
import WebPlusPaymentDetails from "../../components/WebPlusPaymentDetails";
import { useRef } from "react";
import { PaymentSelectionRef } from "../../../paymentSelectionRefs";
import {
  EnrollmentOption,
  PlusPotentialSavingsInfo,
  ShippingAddress,
} from "../../../../models";
import Footer from "../../components/Footer";

import { ModalState } from "../modalState";
import {
  submitAddSubscriptionRequest,
  submitNewSubscriptionEnrollmentRequest,
} from "../utils";
import { Throwable } from "@wss/error-tracking/throwable";

interface StandardSubscriptionFormProps {
  userGuid: string | undefined;
  enrollmentOptions: EnrollmentOption[] | readonly EnrollmentOption[];
  shippingAddresses: readonly ShippingAddress[];
  formattedPlusPrice: string;
  handleShowWelcome: () => void;
  plusPotentialSavingsInfo: PlusPotentialSavingsInfo | undefined;
  modalState: ModalState;
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
  subscriptionChargeDate?: Date;
  addressPlusSignup: boolean;
  isOpenFromAd?: boolean;
}

const StandardSubscriptionForm = ({
  userGuid,
  enrollmentOptions,
  shippingAddresses,
  formattedPlusPrice,
  handleShowWelcome,
  plusPotentialSavingsInfo,
  modalState,
  setModalState,
  subscriptionChargeDate,
  addressPlusSignup,
  isOpenFromAd = false,
}: StandardSubscriptionFormProps) => {
  const paymentSelectionRef = useRef<PaymentSelectionRef>(null);

  const submitForm = async (): Promise<Throwable<void>> => {
    if (modalState.shippingId === 0) {
      setModalState((prev) => ({
        ...prev,
        subscribeCustomErrorMessage: "Please select a shipping address",
      }));
      const error = new Error("A shipping address must be selected");
      return {
        error,
        isError: true,
      };
    }
    setModalState((prev) => ({
      ...prev,
      submitting: true,
      subscribeCustomErrorMessage: "",
    }));
    if (!paymentSelectionRef) {
      return {
        isError: false,
      };
    }

    const tokenizeResult = await paymentSelectionRef.current?.tokenize();
    if (tokenizeResult?.suggestVerifyInformation) {
      setModalState((prev) => ({
        ...prev,
        cardVerificationError: true,
        submitting: false,
      }));
    } else if (!tokenizeResult?.isTokenized) {
      setModalState((prev) => ({
        ...prev,
        cardSaveError: true,
        submitting: false,
      }));
    } else if (addressPlusSignup) {
      return submitAddSubscriptionRequest(
        modalState.shippingId,
        modalState,
        setModalState,
        userGuid,
        handleShowWelcome,
        tokenizeResult.tokenId,
      );
    } else {
      return submitNewSubscriptionEnrollmentRequest(
        modalState,
        setModalState,
        plusPotentialSavingsInfo,
        isOpenFromAd,
        handleShowWelcome,
        tokenizeResult.tokenId,
      );
    }

    return {
      isError: false,
    };
  };

  return (
    <div className="px-6 pb-6 xs:px-10 lm:px-12 md:pb-10 lm:w-[393px]">
      <div className="flex font-bold py-6 lm:pt-10 border-b border-gray-200 text-gray-900 text-2xl lm:text-3xl lm:text-center">
        <span>Get</span>
        <span className="text-green-500 whitespace-pre"> FREE </span>
        <span>shipping & more!</span>
      </div>
      <ShippingDropdown
        isDisabled={false}
        setShippingId={(id) =>
          setModalState((prev) => ({ ...prev, shippingId: id }))
        }
        shippingAddresses={shippingAddresses}
        selectedAddressId={modalState.shippingId}
      />
      <WebPlusPaymentDetails
        enrollmentOptions={enrollmentOptions?.map((option) => option)}
        formattedPlusPrice={formattedPlusPrice}
        setPromotionName={(newPromotionName) =>
          setModalState((prev) => ({
            ...prev,
            promotionName: newPromotionName,
          }))
        }
        promotionName={modalState.promotionName}
        subscriptionChargeDate={subscriptionChargeDate}
      />
      <div className="px-0 pt-6 pb-1 border-0 border-b border-solid border-gray-200">
        <fieldset className="p-0 border-0 lm:flex-col">
          <legend className="w-full p-0 text-base font-bold text-gray-700">
            Payment Method
          </legend>
          <PaymentSelection
            ref={paymentSelectionRef}
            setSavedCardId={(id) =>
              setModalState((prev) => ({ ...prev, savedCardId: id }))
            }
            defaultTokenId={modalState.savedCardId}
            defaultSavedCards={modalState.savedCardData.map((element) => ({
              id: element.num,
              type: element.creditCardType,
              mask: element.lastFour,
              name: element.name,
              isRewardsCard: element.isRewardsCard,
              isDefault: element.default,
            }))}
          />
        </fieldset>
        <Errors
          cardVerificationError={modalState.cardVerificationError}
          cardSaveError={modalState.cardSaveError}
          subscribeError={modalState.subscribeError}
          subscribeCustomErrorMessage={
            modalState.subscribeCustomErrorMessage ?? ""
          }
        />
      </div>
      <Footer
        addressPlusSignup={addressPlusSignup}
        setMarketingOptIn={(value) =>
          setModalState((prev) => ({ ...prev, marketingOptIn: value }))
        }
        marketingOptIn={modalState.marketingOptIn}
        submitting={modalState.submitting}
        submitForm={submitForm}
      />
    </div>
  );
};

export default StandardSubscriptionForm;
